// footer 上传按钮的文件类型与大小校验（对齐 V1 RichTextEditor/index.tsx handleFooterFileChange）。
// V1 行为：选择文件时，单张图片限 10MB（超限剔除并提示），文件总量（含已有）限 50MB（超限截断并提示）。
// 注意：粘贴路径（index.tsx handlePaste）只做 50MB 总量校验、无单图限制 —— 与 V1 pasteHandler 一致，不复用本模块。

export const MAX_FOOTER_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_FOOTER_TOTAL_BYTES = 50 * 1024 * 1024;
export const FOOTER_IMAGE_SIZE_ERROR = '图片大小不能超过 10MB';
export const FOOTER_TOTAL_SIZE_ERROR = '文件总大小不能超过 50MB';
export const FOOTER_UNSUPPORTED_TYPE_ERROR = '文件类型不支持或文件内容与扩展名不一致';

// 仅用于 Footer 二级菜单中的「上传文件或图片」入口，不与粘贴、media-upload 等入口共享。
// 这份白名单只用于「拒」：扩展名不在其中直接剔除。在其中不代表放行，放行由真实内容决定。
const ALLOWED_FOOTER_EXTENSIONS = new Set([
  'pdf', 'xla', 'xlb', 'xlc', 'xlm', 'xls', 'xlsx', 'xlt', 'xlw', 'csv', 'tsv', 'iif',
  'doc', 'docx', 'dot', 'odt', 'rtf', 'pot', 'ppa', 'pps', 'ppt', 'pptx', 'pwz', 'wiz',
  'asm', 'bat', 'c', 'cc', 'conf', 'cpp', 'css', 'cxx', 'def', 'dic', 'eml', 'h', 'hh',
  'htm', 'html', 'ics', 'ifb', 'in', 'js', 'json', 'ksh', 'png', 'jpeg', 'jpg', 'gif',
  'webp', 'md', 'php', 'pkl', 'rb', 'tar', 'tex', 'ts', 'txt', 'xml',
  'mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v', 'xmind', 'zip',
  // 扩充图片格式
  'bmp', 'dib', 'tiff', 'tif', 'ico', 'icns', 'sgi', 'avif', 'heif', 'pic',
  'j2c', 'j2k', 'jp2', 'jpc', 'jpf', 'jpx',
  // 思维导图（FreeMind/XMind）
  'mm',
]);

const IMAGE_EXTENSIONS = new Set([
  'gif', 'jpeg', 'jpg', 'png', 'webp',
  'bmp', 'dib', 'tiff', 'tif', 'ico', 'icns', 'sgi', 'avif', 'heif', 'pic',
  'j2c', 'j2k', 'jp2', 'jpc', 'jpf', 'jpx',
]);

const HEIC_HEIF_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1']);
const AVIF_BRANDS = new Set(['avif', 'avis']);

export type ImageContentType =
  | 'avif'
  | 'bmp'
  | 'gif'
  | 'heif'
  | 'icns'
  | 'ico'
  | 'jpeg'
  | 'jpeg2000'
  | 'png'
  | 'psd'
  | 'sgi'
  | 'svg'
  | 'tiff'
  | 'webp';

// psd / svg 不在放行范围（未列入支持清单）。
const ALLOWED_IMAGE_CONTENT_TYPES = new Set<ImageContentType>([
  'avif', 'bmp', 'gif', 'heif', 'icns', 'ico', 'jpeg', 'jpeg2000', 'png', 'sgi', 'tiff', 'webp',
]);

const readAscii = (bytes: Uint8Array, offset: number, length: number): string =>
  String.fromCharCode(...bytes.slice(offset, offset + length));

const hasBytes = (bytes: Uint8Array, expected: number[], offset = 0): boolean =>
  expected.every((value, index) => bytes[offset + index] === value);

/** 解析 ISO BMFF（mp4/mov/heic/avif 共用容器）的 ftyp brand 列表。
 *  返回 null 表示不是 ISO BMFF；返回空数组表示有 ftyp 但读不出 brand。 */
const readIsoBrands = (bytes: Uint8Array): string[] | null => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let boxOffset = 0;

  while (boxOffset + 8 <= bytes.length) {
    let boxSize = view.getUint32(boxOffset, false);
    const boxType = readAscii(bytes, boxOffset + 4, 4);
    let headerSize = 8;

    if (boxSize === 1) {
      if (boxOffset + 16 > bytes.length) return null;
      const highBits = view.getUint32(boxOffset + 8, false);
      const lowBits = view.getUint32(boxOffset + 12, false);
      boxSize = highBits * 0x100000000 + lowBits;
      if (!Number.isSafeInteger(boxSize)) return null;
      headerSize = 16;
    }
    if (boxSize < headerSize) return null;

    if (boxType === 'ftyp') {
      const availableBoxEnd = Math.min(boxOffset + boxSize, bytes.length);
      const brands: string[] = [];
      for (let offset = boxOffset + headerSize; offset + 4 <= availableBoxEnd; offset += 4) {
        if (offset === boxOffset + headerSize + 4) continue; // 跳过 minor_version
        brands.push(readAscii(bytes, offset, 4));
      }
      return brands;
    }
    if (boxSize === 0 || boxOffset + boxSize > bytes.length) return null;
    boxOffset += boxSize;
  }
  return null;
};

const detectIsoImageType = (bytes: Uint8Array): 'avif' | 'heif' | null => {
  const brands = readIsoBrands(bytes);
  if (!brands) return null;
  if (brands.some((brand) => AVIF_BRANDS.has(brand))) return 'avif';
  if (brands.some((brand) => HEIC_HEIF_BRANDS.has(brand))) return 'heif';
  return null;
};

const hasSvgContent = (bytes: Uint8Array): boolean => {
  const text = new TextDecoder().decode(bytes).replace(/^\uFEFF/, '').trimStart();
  return /^(?:<\?xml[\s\S]*?\?>\s*)?<svg(?:\s|>)/i.test(text);
};

const readFileHeader = (file: File): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('读取文件失败'));
    reader.readAsArrayBuffer(file.slice(0, 4096));
  });

/** 根据真实文件内容识别具体图片格式，不信任 File.type 和文件扩展名。 */
export const detectImageContentType = async (file: File): Promise<ImageContentType | null> => {
  const bytes = await readFileHeader(file);
  if (bytes.length === 0) return null;

  if (hasBytes(bytes, [0xff, 0xd8, 0xff])) return 'jpeg';
  if (hasBytes(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'png';
  if (readAscii(bytes, 0, 6) === 'GIF87a' || readAscii(bytes, 0, 6) === 'GIF89a') return 'gif';
  if (readAscii(bytes, 0, 4) === 'RIFF' && readAscii(bytes, 8, 4) === 'WEBP') return 'webp';
  if (readAscii(bytes, 0, 2) === 'BM') return 'bmp';
  if (hasBytes(bytes, [0x00, 0x00, 0x01, 0x00])) return 'ico';
  if (readAscii(bytes, 0, 4) === '8BPS') return 'psd';
  if (
    hasBytes(bytes, [0x00, 0x00, 0x00, 0x0c, 0x6a, 0x50, 0x20, 0x20, 0x0d, 0x0a, 0x87, 0x0a]) ||
    hasBytes(bytes, [0xff, 0x4f, 0xff, 0x51])
  ) {
    return 'jpeg2000';
  }
  if (hasBytes(bytes, [0x49, 0x49, 0x2a, 0x00]) || hasBytes(bytes, [0x4d, 0x4d, 0x00, 0x2a])) {
    return 'tiff';
  }
  // BigTIFF（.tif 的 64 位变体）
  if (hasBytes(bytes, [0x49, 0x49, 0x2b, 0x00]) || hasBytes(bytes, [0x4d, 0x4d, 0x00, 0x2b])) {
    return 'tiff';
  }
  if (readAscii(bytes, 0, 4) === 'icns') return 'icns';
  // SGI/RGB：魔数 0x01DA
  if (hasBytes(bytes, [0x01, 0xda])) return 'sgi';

  const isoImageType = detectIsoImageType(bytes);
  if (isoImageType) return isoImageType;
  if (hasSvgContent(bytes)) return 'svg';
  return null;
};

export const hasImageContent = async (file: File): Promise<boolean> =>
  (await detectImageContentType(file)) !== null;

/** 非图片的真实内容类型。'text' 是兜底：纯文本格式（txt/md/csv/json/js/html…）没有
 *  magic bytes，内容上无法互相区分，只能整体归为一类。 */
export type BinaryContentType =
  | 'archive' // zip 系：xmind / docx / xlsx / pptx / 普通 zip
  | 'executable'
  | 'ole' // 旧版 office 复合文档：doc / xls / ppt
  | 'pdf'
  | 'rtf'
  | 'tar'
  | 'text'
  | 'video';

/** 允许放行的非图片内容类型。executable 必须缺席——那正是改后缀想蒙过去的东西。 */
const ALLOWED_BINARY_CONTENT_TYPES = new Set<BinaryContentType>([
  'archive', 'ole', 'pdf', 'rtf', 'tar', 'text', 'video',
]);

/** matroska/webm 共用 EBML 头；avi 是 RIFF....AVI␠。 */
const isMatroska = (bytes: Uint8Array): boolean => hasBytes(bytes, [0x1a, 0x45, 0xdf, 0xa3]);
const isRiffAvi = (bytes: Uint8Array): boolean =>
  readAscii(bytes, 0, 4) === 'RIFF' && readAscii(bytes, 8, 4) === 'AVI ';

/** tar 的 ustar 魔数在偏移 257，前 257 字节是文件名等头字段。 */
const isTar = (bytes: Uint8Array): boolean => {
  const magic = readAscii(bytes, 257, 5);
  return magic === 'ustar';
};

/** 内容能否当作纯文本。判据只有「无 NUL 字节」，不做编码校验 —— 中文环境 Excel 导出的
 *  csv 多为 GBK/GB18030，UTF-8 严格解码会失败，不能据此判定非文本。
 *  NUL 是二进制格式的可靠标志，纯文本编码（UTF-8 / GBK / Latin-1…）都不含 NUL。 */
const hasTextContent = (bytes: Uint8Array): boolean => !bytes.includes(0x00);

/** 按真实内容识别非图片文件类型，不看扩展名和 File.type。
 *  调用方应先跑 detectImageContentType——图片由那条路径判定。 */
export const detectBinaryContentType = async (file: File): Promise<BinaryContentType | null> => {
  const bytes = await readFileHeader(file);
  if (bytes.length === 0) return null;

  if (readAscii(bytes, 0, 4) === '%PDF') return 'pdf';
  if (readAscii(bytes, 0, 5) === '{\\rtf') return 'rtf';
  // zip 空档案 PK\x05\x06 与跨卷 PK\x07\x08 也算，xmind/docx 都是标准 PK\x03\x04。
  if (hasBytes(bytes, [0x50, 0x4b]) && [0x03, 0x05, 0x07].includes(bytes[2] ?? -1)) return 'archive';
  if (hasBytes(bytes, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) return 'ole';
  if (hasBytes(bytes, [0x4d, 0x5a])) return 'executable'; // MZ: exe/dll
  if (hasBytes(bytes, [0x7f, 0x45, 0x4c, 0x46])) return 'executable'; // ELF
  if (isMatroska(bytes) || isRiffAvi(bytes)) return 'video';
  if (isTar(bytes)) return 'tar';

  // ISO BMFF 且 brand 不是 heic/avif → 视频（mp4 / mov / m4v 只差 brand，一并放行）。
  const isoBrands = readIsoBrands(bytes);
  if (isoBrands) {
    const isImageBrand = isoBrands.some(
      (brand) => AVIF_BRANDS.has(brand) || HEIC_HEIF_BRANDS.has(brand),
    );
    if (!isImageBrand) return 'video';
  }

  if (hasTextContent(bytes)) return 'text';
  return null;
};

const getFileExtension = (file: File): string =>
  file.name.split('.').pop()?.toLowerCase() ?? '';

/** 仅判断文件是否声称自己是图片；最终是否放行只看 detectImageContentType 的内容结果。 */
export const isFooterImageFile = (file: File): boolean =>
  file.type?.startsWith('image/') ||
  IMAGE_EXTENSIONS.has(getFileExtension(file));

export type FooterFilterResult = {
  accepted: File[];
  imageOversize: boolean; // 有图片超 10MB 被剔除
  totalExceeded: boolean; // 累加触达 50MB 上限被截断
  unsupportedType: boolean; // 文件类型不支持，或图片元数据与真实内容不一致
};

/** 过滤新选文件：先按真实内容判定类型（图片走 detectImageContentType，非图片走
 *  detectBinaryContentType），再执行 10MB 单图和 50MB 总量限制。
 *  扩展名只用于「拒」，任何放行都由内容决定 —— 改后缀不能绕过。 */
export const filterFooterFiles = async (
  existing: File[],
  picked: File[],
): Promise<FooterFilterResult> => {
  let imageOversize = false;
  let unsupportedType = false;
  const validFiles: File[] = [];

  for (const file of picked) {
    let imageContentType: ImageContentType | null = null;
    try {
      imageContentType = await detectImageContentType(file);
    } catch {
      unsupportedType = true;
      continue;
    }
    if (imageContentType && !ALLOWED_IMAGE_CONTENT_TYPES.has(imageContentType)) {
      unsupportedType = true;
      continue;
    }

    const isImage = imageContentType !== null;

    if (isImage) {
      // 声称是文件却是图片内容也放行——图片内容已确认在允许集合内。
      if (file.size > MAX_FOOTER_IMAGE_BYTES) {
        imageOversize = true;
        continue;
      }
      validFiles.push(file);
      continue;
    }

    // 非图片：扩展名只用于「拒」（不在白名单直接拒），放行必须由内容决定。
    // 声称是图片却没有图片内容 —— 伪装，拒。
    if (isFooterImageFile(file) || !ALLOWED_FOOTER_EXTENSIONS.has(getFileExtension(file))) {
      unsupportedType = true;
      continue;
    }

    let binaryContentType: BinaryContentType | null = null;
    try {
      binaryContentType = await detectBinaryContentType(file);
    } catch {
      unsupportedType = true;
      continue;
    }
    if (!binaryContentType || !ALLOWED_BINARY_CONTENT_TYPES.has(binaryContentType)) {
      unsupportedType = true;
      continue;
    }
    validFiles.push(file);
  }

  const currentTotal = existing.reduce((s, f) => s + f.size, 0);
  const accepted: File[] = [];
  let runningTotal = currentTotal;
  let totalExceeded = false;
  for (const file of validFiles) {
    if (runningTotal + file.size > MAX_FOOTER_TOTAL_BYTES) {
      totalExceeded = true;
      break;
    }
    accepted.push(file);
    runningTotal += file.size;
  }
  return { accepted, imageOversize, totalExceeded, unsupportedType };
};
